import React from 'react';
import { Collapse, Label } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import DiffViewer from './DiffViewer';
import './StepDiff.scss';

interface StepDiffProps {
  actionName?: string;
  actual?: any;
  diffsCommand?: { action: string };
  expected?: any;
  fetchName?: string;
  index: number;
  isMismatched: boolean;
  status?: { label: string; bsStyle: 'success' | 'danger' | 'warning' | 'default' };
  stepId?: string | { flowStepDef_id?: string; id?: string };
  stepName?: any;
  stepType?: string;
}

interface StepDiffState {
  expanded: boolean;
}

export function stringifyDiffValue(value: any): string {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
}

class StepDiff extends React.Component<StepDiffProps, StepDiffState> {
  constructor(props: StepDiffProps) {
    super(props);
    this.state = {
      expanded: false,
    };
  }

  componentWillReceiveProps(nextProps: StepDiffProps) {
    if (nextProps.diffsCommand && nextProps.diffsCommand !== this.props.diffsCommand) {
      this.setState({ expanded: nextProps.diffsCommand.action === 'expand' });
    }
  }

  handleToggle = () => {
    this.setState({ expanded: !this.state.expanded });
  };

  render() {
    const { fetchName, actionName, stepId, stepName, stepType, expected, actual, isMismatched, index, status } = this.props;
    const { expanded } = this.state;
    const displayStatus = status || { label: isMismatched ? 'mismatch' : 'passed', bsStyle: isMismatched ? 'danger' : 'success' };
    const contentId = `snapshot-testing-step-diff-${index}`;

    const header = (
      <button
        aria-controls={contentId}
        aria-expanded={expanded}
        className="snapshot-testing-step-diff__header panel-heading"
        onClick={this.handleToggle}
        type="button"
      >
        <span>
          <span className="snapshot-testing-step-diff__index">#{index + 1}</span>
          <strong>{fetchName || actionName || <FormattedMessage defaultMessage="Unnamed Fetch" id="snapshotTesting.unnamedFetch" />}</strong>
          {actionName && fetchName && <span className="snapshot-testing-step-diff__action-name">({actionName})</span>}
          {(stepName || stepId) && (
            <small className="snapshot-testing-step-diff__step-id">
              {stepName || <code>{typeof stepId === 'object' ? (stepId.flowStepDef_id || stepId.id || JSON.stringify(stepId)) : stepId}</code>}
              {stepType && <span className="text-muted"> ({stepType})</span>}
            </small>
          )}
        </span>
        <span>
          <Label bsStyle={displayStatus.bsStyle}>
            {displayStatus.label}
          </Label>
          <i
            className={`fa fa-chevron-${expanded ? 'up' : 'down'} snapshot-testing-step-diff__chevron`}
          />
        </span>
      </button>
    );

    const expectedStr = expanded ? stringifyDiffValue(expected) : '';
    const actualStr = expanded ? stringifyDiffValue(actual) : '';

    return (
      <div className={`snapshot-testing-step-diff step-diff-card panel panel-${isMismatched ? 'danger' : 'default'} ${isMismatched ? 'mismatched' : 'matched'}`}>
        {header}
        <Collapse in={expanded}>
          <div className="panel-body" id={contentId}>
            <div className="step-diff-content">
              <div className="snapshot-testing-step-diff__content-header">
                <div className="snapshot-testing-step-diff__expected-label">
                  <small className="text-muted">
                    <FormattedMessage defaultMessage="Expected" id="snapshotTesting.expectedLabel" />
                  </small>
                </div>
                <div className="snapshot-testing-step-diff__actual-label">
                  <small className="text-muted">
                    <FormattedMessage defaultMessage="Actual" id="snapshotTesting.actualLabel" />
                  </small>
                </div>
              </div>
              {expanded && (
                <DiffViewer
                  actual={actualStr}
                  expected={expectedStr}
                  filename={`step_${index + 1}.json`}
                />
              )}
            </div>
          </div>
        </Collapse>
      </div>
    );
  }
}

export default StepDiff;
